import { Box, Button, CircularProgress, FormControlLabel, List, ListItem, Switch, Typography } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import type { CreditImportController } from '../../hooks/useCreditImports';
import { pluralActivities } from '../../utils/creditImportValidation';

interface Props {
  /** Connected sources (PICPA, CPAx) for the person themselves; nothing renders when empty. */
  connected: CreditImportController[];
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A connected source acts in place: one click imports, the row then says what happened and offers nightly sync. */
function ConnectedSourceRow({ imp }: { imp: CreditImportController }) {
  const { status, displayName, syncing, savingPreference, lastResult, error, syncNow, setAutoSync } = imp;
  const imported = lastResult ? lastResult.created + lastResult.updated : null;
  const line =
    lastResult && imported !== null
      ? imported > 0
        ? `Imported ${pluralActivities(imported)} just now.`
        : lastResult.fetched === 0
          ? `Nothing found for ${status.email}.`
          : 'Up to date.'
      : !status.promptedAt && (status.pendingCreditCount ?? 0) > 0
        ? `${pluralActivities(status.pendingCreditCount ?? 0)} not yet imported.`
        : status.lastSyncAt
          ? `Last imported ${formatDate(status.lastSyncAt)}.`
          : 'Not imported yet.';

  return (
    <ListItem disablePadding divider data-testid={`import-source-${imp.source}`} sx={{ py: 1.5, gap: 2, flexWrap: 'wrap' }}>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {displayName}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {line}
        </Typography>
        {error && (
          <Typography variant="caption" component="div" sx={{ color: 'status.attention.fg', mt: 0.5 }} role="alert">
            {error}
          </Typography>
        )}
      </Box>
      {imported !== null && imported > 0 && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={status.autoSyncEnabled}
              disabled={savingPreference}
              onChange={(_, checked) => void setAutoSync(checked)}
            />
          }
          label={<Typography variant="body2">Keep in sync nightly</Typography>}
          sx={{ mr: 0 }}
        />
      )}
      <Button
        variant="outlined"
        size="small"
        disabled={syncing}
        startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
        onClick={() => void syncNow()}
      >
        {lastResult ? 'Import again' : 'Import now'}
      </Button>
    </ListItem>
  );
}

/**
 * Connected sources at the top of the Upload step, for the person's own history only: each row imports
 * in place, so they sit above the file upload rather than behind a choice. A plain list, no cards.
 */
export default function ConnectedSourcesList({ connected }: Props) {
  if (connected.length === 0) return null;

  return (
    <Box component="section" aria-labelledby="import-connected-heading">
      <Typography id="import-connected-heading" variant="overline" component="h2" sx={{ display: 'block', color: 'text.secondary' }}>
        Connected
      </Typography>
      <List disablePadding sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        {connected.map((imp) => (
          <ConnectedSourceRow key={imp.source} imp={imp} />
        ))}
      </List>
    </Box>
  );
}
